// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Support portal sync API: read-only cockpit endpoints + per-contact
 * portal access actions (grant / revoke / resend invitation).
 *
 * The architecture is PULL-based: CRM exposes a snapshot endpoint and
 * a pending-actions queue, the support portal polls both. CRM never
 * pushes — these admin endpoints are just for the cockpit UI.
 */
import api from './api';

export type SyncStatus = 'green' | 'yellow' | 'red';

export interface SyncHealth {
  status: SyncStatus;
  pending_count: number;
  oldest_pending_age_s: number | null;
  completed_24h: number;
  failed_24h: number;
  last_ack_at: string | null;
  snapshot_endpoint: string | null;
  pending_actions_endpoint: string | null;
  hmac_secret_set: boolean;
}

export interface PendingActionRow {
  id: number;
  action: 'grant' | 'revoke' | 'reissue';
  status: 'pending' | 'completed' | 'failed';
  organisation_external_id: string;
  contact_external_id: string;
  contact_email: string | null;
  contact_name: string | null;
  requested_at: string;
  acked_at: string | null;
  attempts: number;
  last_error: string | null;
  requested_by: { id?: number; name?: string; email?: string };
  full_payload?: Record<string, unknown>;
}

export interface PortalAccessState {
  access_state: 'none' | 'invited' | 'active' | 'revoked';
  invited_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  last_login_at: string | null;
  invitation_sent_count: number;
}

export interface CustomerSyncDetail {
  customer_id: number;
  last_synced_at: string | null;
  pending_count: number;
  actions: PendingActionRow[];
  contacts: Array<{
    id: number;
    full_name: string;
    email: string;
    job_title: string | null;
    portal: PortalAccessState | { access_state: 'none' };
    pending_action: { id: number; action: string; requested_at: string } | null;
  }>;
}

// ── Cockpit ───────────────────────────────────────────────────────

export async function fetchSyncHealth(): Promise<SyncHealth> {
  const res = await api.get('/admin/support_sync/health');
  return res.data.data;
}

export interface ActionsParams {
  status?: string;
  customer_id?: string | number;
  contact_id?: string | number;
  since?: string;
  page?: number;
  per_page?: number;
}

export async function fetchPendingActions(params: ActionsParams = {}) {
  const res = await api.get('/admin/support_sync/actions', { params });
  return res.data as { data: PendingActionRow[]; meta: Record<string, unknown> };
}

export async function fetchPendingAction(id: number): Promise<PendingActionRow> {
  const res = await api.get(`/admin/support_sync/actions/${id}`);
  return res.data.data;
}

export async function retryAction(id: number) {
  const res = await api.post(`/admin/support_sync/actions/${id}/retry`);
  return res.data;
}

export async function fetchCustomerSyncDetail(customerId: number | string): Promise<CustomerSyncDetail> {
  const res = await api.get(`/admin/support_sync/customers/${customerId}`);
  return res.data.data;
}

// ── Per-contact portal access actions ─────────────────────────────

export async function grantPortalAccess(contactId: number | string) {
  const res = await api.post(`/contacts/${contactId}/portal_access`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}

export async function revokePortalAccess(contactId: number | string) {
  const res = await api.delete(`/contacts/${contactId}/portal_access`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}

export async function resendPortalInvitation(contactId: number | string) {
  const res = await api.post(`/contacts/${contactId}/portal_access/resend`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}
